import { Buffer } from "buffer";
// The same specifier receipt-printer.js uses to decode images, so the worker
// bundle carries one copy of pngjs rather than two.
// @ts-ignore — pngjs ships no types for the browser build
import { PNG } from "pngjs/browser.js";
import { COIN_GLYPHS, COIN_GLYPH_SLOT as SLOT } from "./coinGlyphs.generated";

/**
 * A loyalty card drawn as a row of stamps, for the printed receipt.
 *
 * Composited here rather than shipped pre-rendered because the permutations —
 * icon × capacity × filled count — run to several hundred images, and rendered
 * here rather than fetched because a Worker cannot rasterise SVG and the print
 * path should not carry a network round trip. The pieces are committed PNGs
 * (coinGlyphs.generated.ts) plus two shapes cheap enough to plot directly.
 */

/**
 * 12 slots × 48 dots is exactly the 576-dot print width (`-c 48` × charWidth
 * 12). Images print at native size with no scaling, so a wider row would be
 * clipped by the printer rather than shrunk to fit.
 */
const MAX_SLOTS = 12;

/** Outer radius of the empty-slot ring, leaving a gutter inside the slot. */
const RING_RADIUS = SLOT / 2 - 3;
const RING_THICKNESS = 2.5;

type Rgba = { data: Buffer; width: number; height: number };

/**
 * A bare RGBA surface, deliberately *not* a `new PNG()`.
 *
 * Every pngjs PNG instance retains ~9KB of heap for the lifetime of the isolate
 * — it constructs a Parser and a Packer, each holding a zlib stream that is
 * never closed when the object is only used as a pixel buffer. Measured at a
 * flat 9.4KB per instance regardless of image size (a 1×1 leaks as much as a
 * 576×48), which over a few thousand receipts in one Worker isolate is tens of
 * megabytes against a 128MB ceiling.
 *
 * Both of the pngjs entry points used here are duck-typed — `bitblt` copies
 * between `{data,width,height}` and `packer-sync` reads only
 * `{width,height,gamma,data}` — so a plain object works and nothing is retained.
 * `Buffer.alloc` zero-fills, so the surface starts fully transparent, which the
 * ESC/POS threshold resolves to white.
 */
const surface = (width: number, height: number): Rgba => ({
  width,
  height,
  data: Buffer.alloc(width * height * 4),
});

/** Coverage of a pixel by a disc of the given radius, as an alpha in 0..1. */
const discCoverage = (dx: number, dy: number, radius: number) => {
  const d = Math.sqrt(dx * dx + dy * dy);
  return Math.min(Math.max(radius - d + 0.5, 0), 1);
};

/**
 * Plots one slot-sized shape. Anti-aliased rather than hard-edged: the ESC/POS
 * renderer error-diffuses to 1-bit, so partial alpha along a curve dithers into
 * a smoother circle than a stair-stepped hard threshold would give.
 */
const plot = (alphaAt: (dx: number, dy: number) => number): Rgba => {
  const png = surface(SLOT, SLOT);
  const centre = SLOT / 2 - 0.5;
  for (let y = 0; y < SLOT; y++) {
    for (let x = 0; x < SLOT; x++) {
      const alpha = alphaAt(x - centre, y - centre);
      if (alpha <= 0) continue;
      const i = (y * SLOT + x) * 4;
      // Left black; only alpha varies. The printer's threshold multiplies
      // luminance by alpha, so a transparent pixel resolves to white and an
      // unpainted canvas needs no background fill.
      png.data[i + 3] = Math.round(Math.min(alpha, 1) * 255);
    }
  }
  return png;
};

/** An unearned slot: the outline of a stamp yet to be collected. */
const emptySlot = () =>
  plot((dx, dy) =>
    Math.min(
      discCoverage(dx, dy, RING_RADIUS) -
        discCoverage(dx, dy, RING_RADIUS - RING_THICKNESS),
      1,
    ),
  );

/**
 * An earned slot when the program has no icon we hold a glyph for — including
 * the `toll` default, which is a Material Symbols name with no SVG in
 * apps/web/src/images/food_icons/. A solid disc is the honest fallback: it is
 * what a coin looks like, and it cannot be mistaken for an empty slot.
 */
const filledDisc = () => plot((dx, dy) => discCoverage(dx, dy, RING_RADIUS));

/**
 * Copies one slot-sized surface into the row at `slotIndex`.
 *
 * A row-wise `set` rather than pngjs's `bitblt`: bitblt goes through
 * `Buffer#copy`, and the Buffer implementation bundled inside pngjs rejects a
 * target allocated by the host's. `set` is defined on Uint8Array, which both
 * are, and it still throws on an out-of-range write — so bad slot arithmetic
 * fails loudly rather than wrapping around.
 */
const blit = (source: Rgba, target: Rgba, slotIndex: number) => {
  const rowBytes = source.width * 4;
  const stride = target.width * 4;
  const left = slotIndex * rowBytes;
  for (let y = 0; y < source.height; y++) {
    target.data.set(source.data.subarray(y * rowBytes, (y + 1) * rowBytes), y * stride + left);
  }
};

const glyphFor = (icon: string | null | undefined): Rgba => {
  const encoded = icon ? COIN_GLYPHS[icon] : undefined;
  if (!encoded) return filledDisc();
  return PNG.sync.read(Buffer.from(encoded, "base64"));
};

export type StampRow = {
  /** `coin_programs.icon` — a Material Symbols key, or null for the default. */
  icon: string | null | undefined;
  /** Stamps this receipt puts on offer. */
  earned: number;
  /** The redemption tier's requirement, or null when the program has no tier. */
  capacity: number | null;
};

/**
 * Returns a base64 PNG for `{image:…}`, or null when a stamp row cannot
 * usefully be drawn and the caller should print text instead.
 *
 * Null rather than a clipped or shrunken row: a card showing 12 of a 30-stamp
 * tier misstates the offer, which is worse on a receipt than a plain sentence.
 */
export const renderStampRow = ({
  icon,
  earned,
  capacity,
}: StampRow): string | null => {
  // With no tier there is nothing to be "out of", so the row is just the coins
  // on offer. With one, the card is drawn to its full capacity so the gap left
  // to close is visible — that gap is the entire point of a loyalty card.
  const slots = capacity && capacity > 0 ? capacity : earned;
  if (slots <= 0 || slots > MAX_SLOTS) return null;

  const filledCount = Math.min(earned, slots);
  const row = surface(slots * SLOT, SLOT);

  // Each shape is built once and blitted repeatedly — decoding the glyph per
  // slot would be ten decodes of identical bytes.
  const filled = filledCount > 0 ? glyphFor(icon) : null;
  const empty = filledCount < slots ? emptySlot() : null;

  for (let i = 0; i < slots; i++) {
    const source = i < filledCount ? filled : empty;
    if (!source) continue;
    // A copy, not an alpha blend — correct here because slots never overlap.
    blit(source, row, i);
  }

  return PNG.sync.write(row).toString("base64");
};
